import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {catchError, first} from 'rxjs/operators';
import {EMPTY} from 'rxjs';

/**
 * Service to check for duplicate document entries within a specified context, such as a batch type.
 * Utilizes a data service to fetch entries and determine if duplicates exist.
 */
@Injectable({
  providedIn: 'root'
})
export class DuplicateCheckerService {
  constructor(
    private dataService: DataService
  ) {
  }

  /**
   * Checks for a duplicate document number within a specified batch type.
   *
   * @param {string} docNumber - The document number to be checked for duplicates.
   * @param {string} batchType - The type of batch in which to search for the document number.
   * @return {Promise<boolean>} Returns a promise that resolves to true if a duplicate document number is found, otherwise false.
   */
  async checkForDuplicateDocumentNumber(docNumber: string, batchType: string): Promise<boolean> {
    let params = '';
    params += '?search=' + docNumber;
    params += '&batchType=' + batchType;
    params += '&pageNumber=0';
    params += '&pageSize=999999';

    const dupData = await this.fetchDocumentData('entry', params);
    return dupData?.length > 0;
  }

  /**
   * Fetches document data from the specified API endpoint using the given ID.
   *
   * @param {string} endpoint - The API endpoint to fetch data from.
   * @param {string} id - The identifier for the document to be fetched.
   * @return {Promise<any>} A promise that resolves with the fetched document data or rejects with an error.
   */
  private fetchDocumentData(endpoint: string, id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.dataService.getData(endpoint, id)
        .pipe(
          first(),
          catchError(error => {
            console.error(`Error fetching data from ${endpoint}/${id}:`, error);
            reject(error);
            return EMPTY;
          })
        )
        .subscribe(data => resolve(data));
    });
  }
}
